import {
  AfterViewInit,
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  Renderer2,
} from '@angular/core';

/**
 * Appends a drag handle to a <th> element that lets the user resize the column.
 * Widths are set as inline px on the <th> itself — the table grows wider and the
 * nearest overflow-x-auto ancestor scrolls. Nothing outside that container is affected.
 * The minimum width defaults to 120px and can be overridden with
 * `data-col-min-width` on the <th>.
 *
 * Usage: <th baiColResize scope="col">Label</th>
 */
@Directive({
  selector: 'th[baiColResize]',
  standalone: true,
})
export class ColResizeDirective implements AfterViewInit, OnDestroy {
  private static readonly DEFAULT_MIN_WIDTH = 120;
  private _handle!: HTMLElement;
  private _unlisten: (() => void)[] = [];

  constructor(
    private _el: ElementRef<HTMLTableCellElement>,
    private _renderer: Renderer2,
    private _zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    const th = this._el.nativeElement;

    // Ensure the th has position:relative so the handle can be positioned inside it
    this._renderer.setStyle(th, 'position', 'relative');
    this._renderer.setStyle(th, 'user-select', 'none');

    // Create the drag handle
    this._handle = this._renderer.createElement('span') as HTMLElement;
    this._renderer.addClass(this._handle, 'bai-col-resize-handle');
    this._renderer.appendChild(th, this._handle);

    // Run outside Angular zone — these are high-frequency mouse events
    this._zone.runOutsideAngular(() => {
      const onMouseDown = (startEvent: MouseEvent) => {
        startEvent.preventDefault();
        const startX = startEvent.pageX;
        const startWidth = th.offsetWidth;
        const minWidth = this.getMinWidth(th);

        const onMouseMove = (moveEvent: MouseEvent) => {
          const newWidth = Math.max(minWidth, startWidth + moveEvent.pageX - startX);
          th.style.width = `${newWidth}px`;
        };

        const onMouseUp = () => {
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      };

      this._handle.addEventListener('mousedown', onMouseDown);
      this._unlisten.push(() =>
        this._handle.removeEventListener('mousedown', onMouseDown)
      );
    });
  }

  ngOnDestroy(): void {
    this._unlisten.forEach((fn) => fn());
  }

  private getMinWidth(th: HTMLTableCellElement): number {
    const raw = th.getAttribute('data-col-min-width');
    if (!raw) {
      return ColResizeDirective.DEFAULT_MIN_WIDTH;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0
      ? parsed
      : ColResizeDirective.DEFAULT_MIN_WIDTH;
  }
}
